import { CardType } from '@/pages/timeline/types';

export interface StoryBeat {
  title: string;
  description: string;
  type: CardType;
  chapterPosition: number; // Relative position in the story (0-100%)
  suggestedChapterTitle?: string;
}

export interface StoryTemplate {
  id: string;
  name: string;
  description: string;
  recommendedChapters: number;
  beats: StoryBeat[];
}

export const STORY_TEMPLATES: Record<string, StoryTemplate> = {
  hero: {
    id: 'hero',
    name: "Hero's Journey",
    description: "The classic monomyth structure identified by Joseph Campbell, featuring 12 stages of the hero's adventure.",
    recommendedChapters: 12,
    beats: [
      {
        title: "Ordinary World",
        description: "The hero's normal world before the adventure begins.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "The Beginning"
      },
      {
        title: "Call to Adventure",
        description: "The hero is presented with a challenge or quest.",
        type: "plot",
        chapterPosition: 8,
        suggestedChapterTitle: "The Call"
      },
      {
        title: "Refusal of the Call",
        description: "The hero initially refuses the adventure due to fear or other reasons.",
        type: "conflict",
        chapterPosition: 16
      },
      {
        title: "Meeting the Mentor",
        description: "The hero encounters a mentor figure who provides guidance or aid.",
        type: "character",
        chapterPosition: 25,
        suggestedChapterTitle: "The Mentor"
      },
      {
        title: "Crossing the Threshold",
        description: "The hero commits to the adventure and enters the special world.",
        type: "plot",
        chapterPosition: 33,
        suggestedChapterTitle: "New World"
      },
      {
        title: "Tests, Allies, and Enemies",
        description: "The hero faces tests, makes allies, and confronts enemies.",
        type: "conflict",
        chapterPosition: 42
      },
      {
        title: "Approach to the Inmost Cave",
        description: "The hero prepares for the major challenge ahead.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Preparation"
      },
      {
        title: "The Ordeal",
        description: "The hero faces the central crisis of the adventure.",
        type: "conflict",
        chapterPosition: 58,
        suggestedChapterTitle: "The Crisis"
      },
      {
        title: "Reward (Seizing the Sword)",
        description: "The hero achieves the goal and obtains the reward.",
        type: "plot",
        chapterPosition: 67
      },
      {
        title: "The Road Back",
        description: "The hero begins the journey back to the ordinary world.",
        type: "plot",
        chapterPosition: 75,
        suggestedChapterTitle: "The Return"
      },
      {
        title: "Resurrection",
        description: "The hero faces a final test and is transformed by the experience.",
        type: "theme",
        chapterPosition: 83,
        suggestedChapterTitle: "Final Challenge"
      },
      {
        title: "Return with the Elixir",
        description: "The hero returns transformed with something to benefit the ordinary world.",
        type: "theme",
        chapterPosition: 92,
        suggestedChapterTitle: "Homecoming"
      }
    ]
  },
  snowflake: {
    id: 'snowflake',
    name: "Snowflake Method",
    description: "Randy Ingermanson's method for designing and building a novel from a one-sentence summary to a complete manuscript.",
    recommendedChapters: 10,
    beats: [
      {
        title: "One-Sentence Summary",
        description: "A single sentence that summarizes the entire story.",
        type: "theme",
        chapterPosition: 0,
        suggestedChapterTitle: "Core Concept"
      },
      {
        title: "One-Paragraph Summary",
        description: "Expand the sentence into a paragraph with setup, major disasters, and ending.",
        type: "plot",
        chapterPosition: 10,
        suggestedChapterTitle: "Story Overview"
      },
      {
        title: "Character Summaries",
        description: "One-page summaries for each major character, including goals, conflicts, and arcs.",
        type: "character",
        chapterPosition: 20,
        suggestedChapterTitle: "Character Foundations"
      },
      {
        title: "Expanded Synopsis",
        description: "Expand each sentence of the one-paragraph summary into a full paragraph.",
        type: "plot",
        chapterPosition: 30
      },
      {
        title: "Character Descriptions",
        description: "Detailed character descriptions including backstory, motivations, and growth arcs.",
        type: "character",
        chapterPosition: 40,
        suggestedChapterTitle: "Character Development"
      },
      {
        title: "Scene List",
        description: "Create a list of scenes that will make up the novel.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Scene Planning"
      },
      {
        title: "Character Charts",
        description: "Complete character charts for all major and minor characters.",
        type: "character",
        chapterPosition: 60
      },
      {
        title: "Expanded Scene List",
        description: "Expand the scene list with more details about each scene.",
        type: "plot",
        chapterPosition: 70
      },
      {
        title: "First Draft Preparation",
        description: "Organize all materials and prepare to write the first draft.",
        type: "setting",
        chapterPosition: 80,
        suggestedChapterTitle: "Draft Preparation"
      },
      {
        title: "First Draft",
        description: "Write the first draft of the novel using all the prepared materials.",
        type: "plot",
        chapterPosition: 90,
        suggestedChapterTitle: "Writing Phase"
      }
    ]
  },
  storycircle: {
    id: 'storycircle',
    name: "Story Circle",
    description: "Dan Harmon's Story Circle, an adaptation of the Hero's Journey with 8 clear steps in a circular structure.",
    recommendedChapters: 8,
    beats: [
      {
        title: "1. You (Comfort Zone)",
        description: "The character is in their comfort zone, their normal world.",
        type: "character",
        chapterPosition: 0,
        suggestedChapterTitle: "Comfort Zone"
      },
      {
        title: "2. Need (Desire)",
        description: "The character wants something, develops a desire or identifies a need.",
        type: "character",
        chapterPosition: 12.5,
        suggestedChapterTitle: "Desire"
      },
      {
        title: "3. Go (Unfamiliar Situation)",
        description: "The character enters an unfamiliar situation, crossing the threshold.",
        type: "plot",
        chapterPosition: 25,
        suggestedChapterTitle: "Departure"
      },
      {
        title: "4. Search (Adaptation)",
        description: "The character adapts to the new situation, searching for what they need.",
        type: "conflict",
        chapterPosition: 37.5,
        suggestedChapterTitle: "Adaptation"
      },
      {
        title: "5. Find (What They Wanted)",
        description: "The character finds what they were looking for, but at a cost.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Discovery"
      },
      {
        title: "6. Take (Pay the Price)",
        description: "The character pays a heavy price for getting what they wanted.",
        type: "conflict",
        chapterPosition: 62.5,
        suggestedChapterTitle: "Price"
      },
      {
        title: "7. Return (Back to Familiar)",
        description: "The character returns to their familiar situation, changed by the journey.",
        type: "plot",
        chapterPosition: 75,
        suggestedChapterTitle: "Return"
      },
      {
        title: "8. Change (New Self)",
        description: "The character has changed as a result of the journey.",
        type: "theme",
        chapterPosition: 87.5,
        suggestedChapterTitle: "Transformation"
      }
    ]
  },
  sevenpoint: {
    id: 'sevenpoint',
    name: "Seven Point Story Structure",
    description: "A concise story structure with seven key points that create a complete narrative arc.",
    recommendedChapters: 7,
    beats: [
      {
        title: "1. Hook",
        description: "The starting point that introduces the character and their world.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "Beginning"
      },
      {
        title: "2. Plot Turn 1",
        description: "An event that changes the direction of the story and propels the protagonist into action.",
        type: "plot",
        chapterPosition: 14,
        suggestedChapterTitle: "Inciting Incident"
      },
      {
        title: "3. Pinch Point 1",
        description: "The first real pressure applied to the protagonist, often revealing the antagonistic force.",
        type: "conflict",
        chapterPosition: 28,
        suggestedChapterTitle: "First Challenge"
      },
      {
        title: "4. Midpoint",
        description: "The protagonist shifts from reacting to acting, taking control of their situation.",
        type: "plot",
        chapterPosition: 42,
        suggestedChapterTitle: "Turning Point"
      },
      {
        title: "5. Pinch Point 2",
        description: "Increased pressure on the protagonist, often when all seems lost.",
        type: "conflict",
        chapterPosition: 56,
        suggestedChapterTitle: "Major Setback"
      },
      {
        title: "6. Plot Turn 2",
        description: "The protagonist gains the final piece needed to overcome the central conflict.",
        type: "plot",
        chapterPosition: 70,
        suggestedChapterTitle: "Final Piece"
      },
      {
        title: "7. Resolution",
        description: "The protagonist applies what they've learned to resolve the story.",
        type: "theme",
        chapterPosition: 85,
        suggestedChapterTitle: "Conclusion"
      }
    ]
  },
  romancingbeat: {
    id: 'romancingbeat',
    name: "Romancing the Beat",
    description: "Gwen Hayes' romance-specific story structure with beats tailored for developing a romantic relationship.",
    recommendedChapters: 12,
    beats: [
      {
        title: "The Set Up",
        description: "Introduce the main character in their ordinary world.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "Ordinary World"
      },
      {
        title: "The Meet",
        description: "The two main characters meet for the first time.",
        type: "character",
        chapterPosition: 8,
        suggestedChapterTitle: "First Meeting"
      },
      {
        title: "The Cute",
        description: "The characters begin to interact and show interest in each other.",
        type: "character",
        chapterPosition: 16
      },
      {
        title: "The Spark",
        description: "The first hint of chemistry between the characters.",
        type: "character",
        chapterPosition: 24,
        suggestedChapterTitle: "Chemistry"
      },
      {
        title: "The Turning Point",
        description: "Something happens that deepens the relationship.",
        type: "plot",
        chapterPosition: 32,
        suggestedChapterTitle: "Deepening"
      },
      {
        title: "The Glimmer of Hope",
        description: "A moment when the relationship seems possible.",
        type: "theme",
        chapterPosition: 40
      },
      {
        title: "The Point of No Return",
        description: "The characters commit to the relationship despite obstacles.",
        type: "plot",
        chapterPosition: 48,
        suggestedChapterTitle: "Commitment"
      },
      {
        title: "The Midpoint",
        description: "A false victory where everything seems perfect.",
        type: "plot",
        chapterPosition: 56,
        suggestedChapterTitle: "False Victory"
      },
      {
        title: "The Crisis",
        description: "External forces threaten to tear the couple apart.",
        type: "conflict",
        chapterPosition: 64,
        suggestedChapterTitle: "Threat"
      },
      {
        title: "The Black Moment",
        description: "The relationship seems impossible and appears to be over.",
        type: "conflict",
        chapterPosition: 72,
        suggestedChapterTitle: "Breaking Point"
      },
      {
        title: "The Joyful Defeat",
        description: "The characters overcome their internal obstacles.",
        type: "theme",
        chapterPosition: 80,
        suggestedChapterTitle: "Overcoming"
      },
      {
        title: "The Grand Gesture",
        description: "One or both characters make a grand gesture to prove their love.",
        type: "plot",
        chapterPosition: 88,
        suggestedChapterTitle: "Proof of Love"
      },
      {
        title: "The Happily Ever After",
        description: "The couple commits to each other and resolves external conflicts.",
        type: "theme",
        chapterPosition: 96,
        suggestedChapterTitle: "Resolution"
      }
    ]
  },
  storygenius: {
    id: 'storygenius',
    name: "Story Genius Method",
    description: "Lisa Cron's method focusing on the protagonist's internal struggle and transformation.",
    recommendedChapters: 10,
    beats: [
      {
        title: "The Protagonist's Backstory",
        description: "Develop the protagonist's formative experiences and resulting worldview.",
        type: "character",
        chapterPosition: 0,
        suggestedChapterTitle: "Origins"
      },
      {
        title: "The Protagonist's Misbelief",
        description: "Identify the flawed worldview or belief that holds the protagonist back.",
        type: "character",
        chapterPosition: 10,
        suggestedChapterTitle: "Misbelief"
      },
      {
        title: "The Story Problem",
        description: "Create a problem that forces the protagonist to confront their misbelief.",
        type: "conflict",
        chapterPosition: 20,
        suggestedChapterTitle: "The Problem"
      },
      {
        title: "The Protagonist's Goal",
        description: "Define what the protagonist wants and why they want it.",
        type: "character",
        chapterPosition: 30,
        suggestedChapterTitle: "The Goal"
      },
      {
        title: "The Opening Scene",
        description: "Introduce the protagonist in a scene that reveals their misbelief in action.",
        type: "setting",
        chapterPosition: 40,
        suggestedChapterTitle: "Opening"
      },
      {
        title: "The Turning Point",
        description: "The protagonist faces a situation that challenges their misbelief.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Challenge"
      },
      {
        title: "The Escalating Consequences",
        description: "The protagonist's actions based on their misbelief lead to worsening situations.",
        type: "conflict",
        chapterPosition: 60,
        suggestedChapterTitle: "Consequences"
      },
      {
        title: "The Revelation",
        description: "The protagonist begins to see the truth that contradicts their misbelief.",
        type: "theme",
        chapterPosition: 70,
        suggestedChapterTitle: "Revelation"
      },
      {
        title: "The Dark Night of the Soul",
        description: "The protagonist must choose between their misbelief and the new truth.",
        type: "conflict",
        chapterPosition: 80,
        suggestedChapterTitle: "Decision Point"
      },
      {
        title: "The Transformation",
        description: "The protagonist embraces the new truth and acts accordingly.",
        type: "theme",
        chapterPosition: 90,
        suggestedChapterTitle: "Transformation"
      }
    ]
  },
  fichtean: {
    id: 'fichtean',
    name: "Fichtean Curve",
    description: "A structure focused on rising action through a series of crises, with minimal exposition.",
    recommendedChapters: 9,
    beats: [
      {
        title: "Inciting Incident",
        description: "The event that sets the story in motion with immediate action.",
        type: "plot",
        chapterPosition: 0,
        suggestedChapterTitle: "Incident"
      },
      {
        title: "First Crisis",
        description: "The first major obstacle or conflict the protagonist faces.",
        type: "conflict",
        chapterPosition: 12.5,
        suggestedChapterTitle: "First Crisis"
      },
      {
        title: "Rising Action 1",
        description: "The protagonist attempts to resolve the crisis but faces complications.",
        type: "plot",
        chapterPosition: 25
      },
      {
        title: "Second Crisis",
        description: "A more significant obstacle that raises the stakes.",
        type: "conflict",
        chapterPosition: 37.5,
        suggestedChapterTitle: "Second Crisis"
      },
      {
        title: "Rising Action 2",
        description: "The protagonist struggles with increased intensity against mounting odds.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Mounting Tension"
      },
      {
        title: "Third Crisis",
        description: "The most severe crisis that pushes the protagonist to their limits.",
        type: "conflict",
        chapterPosition: 62.5,
        suggestedChapterTitle: "Major Crisis"
      },
      {
        title: "Rising Action 3",
        description: "The final push toward resolution with maximum tension.",
        type: "plot",
        chapterPosition: 75
      },
      {
        title: "Climax",
        description: "The final confrontation where the main conflict is resolved.",
        type: "conflict",
        chapterPosition: 87.5,
        suggestedChapterTitle: "Climax"
      },
      {
        title: "Denouement",
        description: "A brief resolution showing the aftermath of the climax.",
        type: "theme",
        chapterPosition: 95,
        suggestedChapterTitle: "Resolution"
      }
    ]
  },
  shortjourney: {
    id: 'shortjourney',
    name: "Hero's Journey for Short Stories",
    description: "A condensed version of the Hero's Journey adapted for short stories and novellas.",
    recommendedChapters: 6,
    beats: [
      {
        title: "Ordinary World & Call to Adventure",
        description: "Introduce the hero and their world, then present them with a challenge or opportunity.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "The Beginning"
      },
      {
        title: "Refusal & Meeting the Mentor",
        description: "The hero hesitates but receives guidance that helps them commit to the journey.",
        type: "character",
        chapterPosition: 20,
        suggestedChapterTitle: "Guidance"
      },
      {
        title: "Crossing the Threshold & Tests",
        description: "The hero enters the special world and faces initial challenges.",
        type: "plot",
        chapterPosition: 40,
        suggestedChapterTitle: "Challenges"
      },
      {
        title: "Approach & Ordeal",
        description: "The hero prepares for and faces the central crisis of the story.",
        type: "conflict",
        chapterPosition: 60,
        suggestedChapterTitle: "The Crisis"
      },
      {
        title: "Reward & The Road Back",
        description: "The hero achieves their goal but must deal with the consequences.",
        type: "plot",
        chapterPosition: 80,
        suggestedChapterTitle: "Achievement"
      },
      {
        title: "Resurrection & Return",
        description: "The hero faces a final test and returns transformed.",
        type: "theme",
        chapterPosition: 95,
        suggestedChapterTitle: "Transformation"
      }
    ]
  },
  savecat: {
    id: 'savecat',
    name: "Save the Cat",
    description: "Blake Snyder's 15-beat plot structure widely used in screenwriting.",
    recommendedChapters: 10,
    beats: [
      {
        title: "Opening Image",
        description: "A visual that sets the tone and mood of the story.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "Opening"
      },
      {
        title: "Theme Stated",
        description: "What your story is about; the message, the truth.",
        type: "theme",
        chapterPosition: 5
      },
      {
        title: "Setup",
        description: "Introduce the main character and their world.",
        type: "setting",
        chapterPosition: 10
      },
      {
        title: "Catalyst",
        description: "The inciting incident that changes everything.",
        type: "plot",
        chapterPosition: 15,
        suggestedChapterTitle: "The Incident"
      },
      {
        title: "Debate",
        description: "The protagonist wrestles with the decision to act.",
        type: "conflict",
        chapterPosition: 20
      },
      {
        title: "Break into Two",
        description: "The protagonist decides to take action and enters a new world.",
        type: "plot",
        chapterPosition: 25,
        suggestedChapterTitle: "Decision"
      },
      {
        title: "B Story",
        description: "A secondary story that often carries the theme and introduces a love interest.",
        type: "character",
        chapterPosition: 30
      },
      {
        title: "Fun and Games",
        description: "The promise of the premise is delivered. This is what the trailer shows.",
        type: "plot",
        chapterPosition: 40,
        suggestedChapterTitle: "Promise"
      },
      {
        title: "Midpoint",
        description: "The fun and games are over. Stakes are raised.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Turning Point"
      },
      {
        title: "Bad Guys Close In",
        description: "External and internal pressures mount.",
        type: "conflict",
        chapterPosition: 60
      },
      {
        title: "All Is Lost",
        description: "The protagonist hits rock bottom.",
        type: "conflict",
        chapterPosition: 70,
        suggestedChapterTitle: "Darkest Hour"
      },
      {
        title: "Dark Night of the Soul",
        description: "The protagonist must dig deep to find the strength to continue.",
        type: "theme",
        chapterPosition: 75
      },
      {
        title: "Break into Three",
        description: "The protagonist finds the solution and takes action.",
        type: "plot",
        chapterPosition: 80,
        suggestedChapterTitle: "Resolution"
      },
      {
        title: "Finale",
        description: "The protagonist proves they've changed and solved the problem.",
        type: "plot",
        chapterPosition: 90,
        suggestedChapterTitle: "Climax"
      },
      {
        title: "Final Image",
        description: "Mirror of the opening image that shows how much has changed.",
        type: "setting",
        chapterPosition: 95
      }
    ]
  },
  threeact: {
    id: 'threeact',
    name: "Three-Act Structure",
    description: "The classic three-act structure used in countless stories and screenplays.",
    recommendedChapters: 8,
    beats: [
      {
        title: "Act 1: Exposition",
        description: "Introduce the characters, setting, and the main conflict.",
        type: "setting",
        chapterPosition: 0,
        suggestedChapterTitle: "Introduction"
      },
      {
        title: "Inciting Incident",
        description: "The event that sets the story in motion.",
        type: "plot",
        chapterPosition: 12,
        suggestedChapterTitle: "The Trigger"
      },
      {
        title: "First Plot Point",
        description: "The protagonist commits to addressing the conflict.",
        type: "plot",
        chapterPosition: 25,
        suggestedChapterTitle: "Point of No Return"
      },
      {
        title: "Act 2: Rising Action",
        description: "The protagonist faces obstacles and complications.",
        type: "conflict",
        chapterPosition: 30,
        suggestedChapterTitle: "Complications"
      },
      {
        title: "Midpoint",
        description: "A major revelation or reversal that changes the direction of the story.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "Revelation"
      },
      {
        title: "Second Plot Point",
        description: "The final piece of information needed to resolve the conflict.",
        type: "plot",
        chapterPosition: 75,
        suggestedChapterTitle: "Final Piece"
      },
      {
        title: "Act 3: Climax",
        description: "The protagonist confronts the main conflict.",
        type: "conflict",
        chapterPosition: 85,
        suggestedChapterTitle: "Final Confrontation"
      },
      {
        title: "Resolution",
        description: "The conflict is resolved and loose ends are tied up.",
        type: "theme",
        chapterPosition: 95,
        suggestedChapterTitle: "Conclusion"
      }
    ]
  },
  mystery: {
    id: 'mystery',
    name: "Mystery/Detective",
    description: "A structure for mystery stories featuring a detective and an investigation.",
    recommendedChapters: 10,
    beats: [
      {
        title: "The Crime",
        description: "The crime or puzzle that needs to be solved is presented.",
        type: "conflict",
        chapterPosition: 0,
        suggestedChapterTitle: "The Crime"
      },
      {
        title: "Enter the Detective",
        description: "Introduce the detective character and their methods.",
        type: "character",
        chapterPosition: 10,
        suggestedChapterTitle: "The Detective"
      },
      {
        title: "Investigation Begins",
        description: "The detective starts gathering clues and information.",
        type: "plot",
        chapterPosition: 20
      },
      {
        title: "Meet the Suspects",
        description: "Introduce potential suspects and their motives.",
        type: "character",
        chapterPosition: 30,
        suggestedChapterTitle: "The Suspects"
      },
      {
        title: "First Red Herring",
        description: "A misleading clue or false lead.",
        type: "plot",
        chapterPosition: 40
      },
      {
        title: "Midpoint Twist",
        description: "A major revelation that changes the direction of the investigation.",
        type: "plot",
        chapterPosition: 50,
        suggestedChapterTitle: "The Twist"
      },
      {
        title: "Raise the Stakes",
        description: "The stakes are raised, often through another crime or threat.",
        type: "conflict",
        chapterPosition: 60
      },
      {
        title: "Detective in Peril",
        description: "The detective faces personal danger or a major setback.",
        type: "conflict",
        chapterPosition: 70,
        suggestedChapterTitle: "Peril"
      },
      {
        title: "Key Revelation",
        description: "The detective discovers the crucial piece of evidence.",
        type: "plot",
        chapterPosition: 80,
        suggestedChapterTitle: "Revelation"
      },
      {
        title: "The Solution",
        description: "The detective reveals the solution and explains the mystery.",
        type: "plot",
        chapterPosition: 90,
        suggestedChapterTitle: "Solution"
      }
    ]
  },
  romance: {
    id: 'romance',
    name: "Romance Arc",
    description: "A classic structure for romance stories focusing on the relationship development.",
    recommendedChapters: 8,
    beats: [
      {
        title: "Meet Cute",
        description: "The main characters meet in an interesting or memorable way.",
        type: "character",
        chapterPosition: 0,
        suggestedChapterTitle: "First Meeting"
      },
      {
        title: "Attraction",
        description: "The characters feel drawn to each other despite initial resistance.",
        type: "character",
        chapterPosition: 15
      },
      {
        title: "Relationship Development",
        description: "The characters get to know each other better.",
        type: "character",
        chapterPosition: 30,
        suggestedChapterTitle: "Getting Closer"
      },
      {
        title: "First Kiss/Commitment",
        description: "A significant romantic milestone is reached.",
        type: "plot",
        chapterPosition: 45
      },
      {
        title: "Midpoint Conflict",
        description: "External forces threaten the relationship.",
        type: "conflict",
        chapterPosition: 50,
        suggestedChapterTitle: "Obstacles"
      },
      {
        title: "Retreat",
        description: "One or both characters pull away due to fear or external pressure.",
        type: "conflict",
        chapterPosition: 65
      },
      {
        title: "Dark Moment",
        description: "The relationship seems impossible and appears to be over.",
        type: "conflict",
        chapterPosition: 80,
        suggestedChapterTitle: "Separation"
      },
      {
        title: "Resolution",
        description: "The characters overcome obstacles and commit to each other.",
        type: "theme",
        chapterPosition: 95,
        suggestedChapterTitle: "Reunited"
      }
    ]
  }
};

export const getTemplateName = (templateId: string): string => {
  if (templateId.startsWith('user_')) {
    // Handle user templates
    const id = templateId.replace('user_', '');
    // This would need actual implementation to fetch from user templates
    return `Custom Template (${id})`;
  }

  return STORY_TEMPLATES[templateId]?.name || 'Unknown Template';
};

export const getTemplateDescription = (templateId: string): string => {
  if (templateId.startsWith('user_')) {
    // Handle user templates
    // This would need actual implementation to fetch from user templates
    return 'Custom user-created template';
  }

  return STORY_TEMPLATES[templateId]?.description || 'No description available';
};

export const getTemplateBeats = (templateId: string): StoryBeat[] => {
  if (templateId.startsWith('user_')) {
    // Handle user templates
    // This would need actual implementation to fetch from user templates
    return [];
  }

  return STORY_TEMPLATES[templateId]?.beats || [];
};